import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, EyeOff, ArrowLeft, AlertTriangle, KeyRound } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoHead, SEO_PRESETS } from "@/components/SeoHead";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sessionReplaced, setSessionReplaced] = useState(false);

  useEffect(() => {
    if (searchParams.get("session_replaced") === "1") {
      setSessionReplaced(true);
      // Clean the URL after reading
      const next = new URLSearchParams(searchParams);
      next.delete("session_replaced");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setSubmitting(true);
    setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    toast.success("Welcome!");
    const from = location.state?.from?.pathname;
    if (res.user.role === "admin") {
      navigate(from && from.startsWith("/admin") ? from : "/admin", { replace: true });
    } else {
      navigate(from && from.startsWith("/student") ? from : "/student/dashboard", { replace: true });
    }
  };

  return (
    <div className="min-h-screen relative bg-background overflow-x-hidden">
      <SeoHead {...SEO_PRESETS.login} />
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="glow-orb glow-orb-teal w-[600px] h-[600px] -top-40 -right-40" />
        <div className="glow-orb glow-orb-navy w-[500px] h-[500px] -bottom-32 -left-32" />
      </div>

      <header className="relative z-10">
        <div className="container flex h-16 sm:h-20 items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button asChild variant="ghost" data-testid="login-back-home">
              <Link to="/"><ArrowLeft className="h-4 w-4" /> Home</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 px-4 py-10 sm:py-16 flex items-center justify-center">
        <div className="w-full max-w-md">
          <Card className="glass-card relative overflow-hidden">
            {/* Accent edge */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
            <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-primary/30 blur-3xl pointer-events-none" />
            <CardContent className="p-7 sm:p-10 relative">
            <div className="text-center mb-7">
              <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">
                Sign in to <span className="text-gradient-teal">Portal eLearningSaham</span>
              </h1>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">by Maahad Bursa</p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Access your courses, learning videos, PDF notes, and student resources.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              {sessionReplaced && (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 flex items-start gap-2" data-testid="session-replaced-banner">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>Your account was signed in from another device. Please sign in again.</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="login-toggle-pwd"
                    tabIndex={-1}
                    aria-label="Toggle password visibility"
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-sm text-destructive" data-testid="login-error">{error}</p>
              )}

              <Button type="submit" className="w-full" size="lg" disabled={submitting} data-testid="login-submit">
                {submitting ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-border space-y-3">
              <Button asChild variant="outline" className="w-full" data-testid="login-first-time-btn">
                <Link to="/create-password">
                  <KeyRound className="h-4 w-4" /> First Time Login / Create Password
                </Link>
              </Button>
              <p className="text-sm text-center text-muted-foreground">
                Don't have an account yet?{" "}
                <Link to="/register" className="text-primary hover:underline font-medium" data-testid="login-register-link">
                  Register here
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
        </div>
      </main>
    </div>
  );
}
